/**
 * Web client wrappers for the Coverage Phase B v2 API surface.
 *
 * Mirrors the server contract in apps/api/src/services/coverage-v2.ts and
 * apps/api/src/routes/interviews.ts. Contract types live in
 * `@legacyorb/shared-types`; this file only owns query keys and the
 * fetch/mutate functions that bind those types to the web `apiClient`.
 */
import type { CardActionResponse, NextTopic } from '@legacyorb/shared-types';
import { apiClient } from '@/services/api-client';

// Re-export so existing web call sites (e.g. CoverageMap.tsx) that imported
// the v2 types from `@/services/coverage-v2` keep compiling.
export type {
  CoverageCardState,
  CoverageCard,
  CoveragePeriod,
  CoverageV2Response,
  NextTopic,
  CardActionResponse,
} from '@legacyorb/shared-types';

// ─── User-action mutations (Sprint 6 Prompt 6.3) ──────────────────────────

async function postCardAction(
  cardId: string,
  action: 'complete' | 'reopen' | 'revive'
): Promise<CardActionResponse> {
  const res = await apiClient.post<CardActionResponse>(
    `/api/v1/interviews/cards/${encodeURIComponent(cardId)}/${action}`,
    {}
  );
  if (!res.success) {
    throw new Error(res.error?.message ?? `Failed to ${action} card`);
  }
  return res.data as CardActionResponse;
}

export const completeCard = (cardId: string) => postCardAction(cardId, 'complete');
export const reopenCard = (cardId: string) => postCardAction(cardId, 'reopen');
export const reviveCard = (cardId: string) => postCardAction(cardId, 'revive');

// ─── Next-topics fetch (Sprint 6 Prompt 6.5) ──────────────────────────────

export async function fetchNextTopics(k = 3): Promise<NextTopic[]> {
  const res = await apiClient.get<NextTopic[]>('/api/v1/interviews/next-topics', {
    k: String(k),
  });
  if (!res.success) {
    throw new Error(res.error?.message ?? 'Failed to load next topics');
  }
  return (res.data as unknown as NextTopic[]) ?? [];
}

export function nextTopicsQueryKey(targetUserId?: string | null) {
  return ['interviews', 'next-topics', targetUserId ?? 'self'] as const;
}
